// HeroV2Section — preview-only redesign of the above-the-fold hero.
// Mounted on /preview/hero-v2. The live homepage continues to render
// HeroSection until the user approves this preview.
//
// Layout (desktop, 1280+):
//   ┌─────────────────────────────────────────────────────────────┐
//   │  [black card 45% w]      │   [Karine reception photo 55%]   │
//   │  · Ottawa's Luxury       │                                  │
//   │  · Medical Aesthetics    │                                  │
//   │    Redefined (script)    │                                  │
//   │  · Expert. Natural. You. │                                  │
//   │  · BOTOX · FILLERS · …   │                                  │
//   │  · [BOOK YOUR COMP… CTA] │                                  │
//   │  · ★★★★★ 1,000+ reviews │                                  │
//   └─────────────────────────────────────────────────────────────┘
//   ┌─────────────────────────────────────────────────────────────┐
//   │  4 clickable feature cards (Crown · Afterpay · Refer · Vrt) │
//   └─────────────────────────────────────────────────────────────┘
//   ┌─────────────────────────────────────────────────────────────┐
//   │  PRECISION · ADVANCED TECH · LUXURY EXPERIENCE (dark strip) │
//   └─────────────────────────────────────────────────────────────┘
//
// Every feature card is a deep-linked <Link> so taps go straight to the
// right page (no scroll-to-section guessing).

import { Link } from "react-router-dom";
import { ArrowRight, Crown, Gift, CreditCard, Calendar, Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

// HERO_IMG should be a clean, raw photo of Karine at her reception desk
// (the same composition as the mockup: standing left of the marble desk,
// KL logo on the wall behind, lit shelving and lamp). Waiting on the user
// to upload the original photo. Until then we render an empty branded
// panel so the layout doesn't ship with a broken / nested image.
// Raw reception photo of Karine (1080x1080, no UI overlays) — provided by client.
const HERO_IMG = "https://customer-assets.emergentagent.com/job_8776705b-3965-48ce-aa62-68a5febfba36/artifacts/xwmbt4dc_1080x1080.png";
const HERO_IMG_FALLBACK = "https://images.unsplash.com/photo-1631730486572-226d1f595b68?w=1400&q=80&auto=format&fit=crop";

const HeroV2Section = ({ openBookingModal, openVirtualConsultation }) => {
  const { language } = useLanguage();
  const isFr = language === "fr";

  const features = [
    {
      id: "crown",
      title: isFr ? "Récompenses Crown Reserve" : "Crown Reserve Rewards",
      body: isFr
        ? "Gagnez des points à chaque visite avec des avantages exclusifs aux membres."
        : "Earn points on every visit with exclusive member benefits.",
      cta: isFr ? "En savoir plus" : "Learn more",
      to: "/rewards",
      icon: Crown,
      iconBg: "bg-zinc-900",
      iconRing: "ring-amber-400/40",
      iconColor: "text-amber-300",
    },
    {
      id: "afterpay",
      title: isFr ? "Afterpay disponible" : "Afterpay Available",
      body: isFr
        ? "Payez en 4 versements sans intérêt. Simple et sécuritaire."
        : "Pay in 4 interest-free installments. Easy & secure.",
      cta: isFr ? "En savoir plus" : "Learn more",
      to: "/afterpay",
      icon: CreditCard,
      iconBg: "bg-[#B2FCE4]",
      iconRing: "ring-emerald-400/40",
      iconColor: "text-emerald-900",
    },
    {
      id: "refer",
      title: isFr ? "Parrainez un ami" : "Refer a Friend",
      body: isFr
        ? "Donnez 50$, recevez 50$ quand ils complètent leur traitement."
        : "Give $50, Get $50 when they complete their treatment.",
      cta: isFr ? "En savoir plus" : "Learn more",
      to: "/refer",
      icon: Gift,
      iconBg: "bg-emerald-900",
      iconRing: "ring-emerald-500/40",
      iconColor: "text-amber-300",
    },
    {
      id: "virtual",
      title: isFr ? "Consultations virtuelles" : "Virtual Consults",
      body: isFr
        ? "Soins personnalisés depuis le confort de votre maison."
        : "Personalized care from the comfort of your home.",
      cta: isFr ? "Réservez" : "Book Now",
      to: "#",
      onClick: openVirtualConsultation,
      icon: Calendar,
      iconBg: "bg-zinc-900",
      iconRing: "ring-amber-400/40",
      iconColor: "text-amber-300",
    },
  ];

  return (
    <section className="bg-[#FBF6EE]" data-testid="hero-v2">
      {/* ───────────────────────── ABOVE-THE-FOLD HERO ───────────────────────── */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-0 rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.35)]">
          {/* LEFT — black panel with copy + CTA */}
          <div
            className="relative bg-[#0E0B07] text-white p-8 sm:p-10 lg:p-12 flex flex-col justify-center"
            data-testid="hero-v2-left"
          >
            {/* Subtle gold corner glow */}
            <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full bg-amber-500/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-amber-500/5 blur-3xl" />

            {/* Eyebrow */}
            <div className="mb-6">
              <span className="text-[#C9A567] text-[12px] font-bold uppercase tracking-[0.42em]">
                {isFr ? "Le luxe d'Ottawa" : "Ottawa's Luxury"}
              </span>
            </div>

            {/* Headline */}
            <h1
              className="text-[40px] sm:text-[46px] lg:text-[52px] xl:text-[58px] leading-[1.02] mb-1"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
              data-testid="hero-v2-headline"
            >
              {isFr ? (
                <span className="block whitespace-nowrap">Esthétique Médicale</span>
              ) : (
                <span className="block whitespace-nowrap">Medical Aesthetics</span>
              )}
              <span
                className="block italic text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-amber-400 to-amber-200 mt-2 text-[54px] sm:text-[62px] lg:text-[72px] xl:text-[82px] leading-[0.95]"
                style={{ fontFamily: "'Pinyon Script', 'Allura', 'Great Vibes', cursive", fontWeight: 400 }}
              >
                {isFr ? "Redéfinie" : "Redefined"}
              </span>
            </h1>

            {/* Sub-line */}
            <p className="text-zinc-300 text-base sm:text-lg mt-6 mb-7 max-w-md uppercase tracking-[0.18em]">
              {isFr
                ? "Soins experts. Résultats naturels. Vous."
                : "Expert Care. Natural Results. You."}
            </p>

            {/* Treatment chips row */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-zinc-200 text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-9">
              {["BOTOX", "FILLERS", "POTENZA", "LASER", "HYDRAFACIAL"].map((t, i) => (
                <span key={t} className="flex items-center gap-3">
                  {i > 0 && <span className="text-amber-400/60">·</span>}
                  <Link
                    to={
                      t === "BOTOX"
                        ? "/menu#neuromodulator"
                        : t === "FILLERS"
                        ? "/dermal-fillers"
                        : t === "POTENZA"
                        ? "/laser-and-skin#rf-microneedling"
                        : t === "LASER"
                        ? "/laser-and-skin"
                        : "/hydrafacial"
                    }
                    className="hover:text-amber-300 transition-colors"
                    data-testid={`hero-v2-chip-${t.toLowerCase()}`}
                  >
                    {t}
                  </Link>
                </span>
              ))}
            </div>

            {/* Primary CTA */}
            <button
              type="button"
              onClick={openBookingModal}
              className="group inline-flex items-center justify-between gap-4 w-full max-w-[440px] px-7 py-4 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 text-black font-extrabold uppercase tracking-[0.18em] text-sm shadow-[0_15px_40px_-10px_rgba(245,158,11,0.6)] hover:shadow-[0_20px_50px_-10px_rgba(245,158,11,0.85)] hover:-translate-y-0.5 transition-all duration-300"
              data-testid="hero-v2-primary-cta"
            >
              <span>
                {isFr ? "Réservez une consultation gratuite" : "Book Your Complimentary Consultation"}
              </span>
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>

            {/* Star trust row */}
            <div className="mt-8 flex items-center gap-3" data-testid="hero-v2-stars">
              <div className="flex items-center gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-zinc-300 text-sm">
                {isFr ? "Plus de 1 000 expériences client 5 étoiles" : "Over 1,000 Five-Star Client Experiences"}
              </span>
            </div>
          </div>

          {/* RIGHT — hero photo */}
          <div className="relative min-h-[420px] lg:min-h-[640px] bg-zinc-200 overflow-hidden" data-testid="hero-v2-right">
            <img
              src={HERO_IMG || HERO_IMG_FALLBACK}
              onError={(e) => {
                if (e.currentTarget.src !== HERO_IMG_FALLBACK) e.currentTarget.src = HERO_IMG_FALLBACK;
              }}
              alt={isFr ? "Karine Lauzon dans sa clinique" : "Karine Lauzon at her clinic reception"}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
            {/* Soft vignette so the photo edges blend with the panel border */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-black/10" />
          </div>
        </div>
      </div>

      {/* ───────────────── 4 FEATURE BOXES (every box clickable) ───────────────── */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 mt-6 sm:mt-8" data-testid="hero-v2-features">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {features.map((f) => {
            const Icon = f.icon;
            const inner = (
              <div className="h-full flex items-start gap-4 px-5 py-5 bg-white border border-amber-200/60 rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group">
                <div className={`w-12 h-12 rounded-full ${f.iconBg} ring-1 ${f.iconRing} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-5 h-5 ${f.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-zinc-900 mb-1.5">
                    {f.title}
                  </h3>
                  <p className="text-[13px] text-zinc-600 leading-snug">
                    {f.body}
                  </p>
                  <span className="inline-flex items-center gap-1 mt-3 text-[11px] font-extrabold uppercase tracking-[0.16em] text-amber-700 group-hover:text-amber-900 transition-colors">
                    {f.cta} <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
            );
            return f.onClick ? (
              <button
                key={f.id}
                type="button"
                onClick={f.onClick}
                className="text-left"
                data-testid={`hero-v2-feature-${f.id}`}
              >
                {inner}
              </button>
            ) : (
              <Link
                key={f.id}
                to={f.to}
                className="block"
                data-testid={`hero-v2-feature-${f.id}`}
              >
                {inner}
              </Link>
            );
          })}
        </div>
      </div>

      {/* ─────────────────────── BOTTOM TAG STRIP ─────────────────────── */}
      <div className="mt-10 sm:mt-14 bg-[#F1E6CC] border-t border-[#E2D3B0]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-10 text-[11px] sm:text-[12px] uppercase tracking-[0.34em] font-semibold text-zinc-800">
          <span>{isFr ? "Infirmière injectrice de précision" : "Precision Injectionist"}</span>
          <span className="text-[#C9A567] hidden sm:inline">•</span>
          <span>{isFr ? "Technologie avancée" : "Advanced Technology"}</span>
          <span className="text-[#C9A567] hidden sm:inline">•</span>
          <span>{isFr ? "Expérience de luxe" : "Luxury Experience"}</span>
        </div>
      </div>
    </section>
  );
};

export default HeroV2Section;
